import React, { useState, useEffect, useCallback } from 'react';
import { useNavigation } from '../contexts/NavigationContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n/hooks/useTranslation';
import { MetricWidget } from './admin/MetricWidgets';
import { WorkflowProcessWidget } from './admin/WorkflowProcessWidget';
import { ConstructionBanner } from './ConstructionBanner';
import { 
  Briefcase, 
  Users, 
  Clock, 
  FileText,
  TrendingUp,
  Calendar,
  Settings,
  GripVertical,
  Save,
  Plus,
  Eye,
  EyeOff,
  MessageCircle,
  Mail,
  Bell
} from 'lucide-react';

interface DashboardWidget {
  id: string;
  type: 'quickAction' | 'rightMenuAction';
  label: string;
  icon: string;
  path?: string;
  action?: string;
  description?: string;
  color?: string;
  order: number;
  enabled: boolean;
}

const iconMap: Record<string, any> = {
  Briefcase,
  Users,
  Clock,
  FileText,
  Calendar,
  TrendingUp,
  MessageCircle,
  Mail,
  Bell
};

export const Dashboard: React.FC = () => {
  const { navigate } = useNavigation();
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  const { t, language } = useTranslation();
  const [quickActions, setQuickActions] = useState<DashboardWidget[]>([]);
  const [allWidgets, setAllWidgets] = useState<DashboardWidget[]>([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [systemMetrics, setSystemMetrics] = useState<any>(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const canEdit = hasPermission('Dashboard', 'edit');
  const isSystemAdmin = user?.role === 'System Administrator';

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/subscriptions/current`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      // Subscription status fetched but not stored (removed unused state)
    } catch (err) {
      // Silently fail
    }
  }, [apiUrl]);

  const fetchSystemMetrics = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/system-metrics`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSystemMetrics(data.metrics);
        }
      }
    } catch (err) {
      console.error('[Dashboard] Error fetching system metrics:', err);
    }
  }, [apiUrl]);

  const fetchDashboardSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/dashboard-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        const widgets = data.data.widgets || [];
        const quickActionWidgets = widgets.filter((w: DashboardWidget) => w.type === 'quickAction');
        
        // Merge with defaults to ensure all available widgets are present
        // Always use translated labels/descriptions from defaults, but preserve order/enabled state from database
        const defaults = getDefaultQuickActions();
        const mergedWidgets = defaults.map(defaultWidget => {
          const existing = quickActionWidgets.find((w: DashboardWidget) => w.id === defaultWidget.id);
          if (existing) {
            // Use translated labels/descriptions/icons from defaults, but keep order/enabled state from database
            return {
              ...existing,
              label: defaultWidget.label, // Use translated label
              description: defaultWidget.description, // Use translated description
              icon: defaultWidget.icon || existing.icon, // Use default icon if available, otherwise keep existing
              color: defaultWidget.color || existing.color // Use default color if available, otherwise keep existing
            };
          }
          return defaultWidget;
        });
        
        setAllWidgets(widgets);
        setQuickActions(mergedWidgets);
      }
    } catch (err) {
      console.error('Failed to fetch dashboard settings:', err);
      // Fall back to default actions on error
      const defaults = getDefaultQuickActions();
      setAllWidgets(defaults);
      setQuickActions(defaults);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);

  useEffect(() => {
    if (user) {
      fetchDashboardSettings();
      fetchSubscriptionStatus();
      // Fetch system metrics once for System Admins
      if (isSystemAdmin) {
        fetchSystemMetrics();
      }
    }
  }, [user, isSystemAdmin, fetchDashboardSettings, fetchSubscriptionStatus, fetchSystemMetrics]);

  const handleWidgetClick = (action: DashboardWidget) => {
    if (editMode) return;

    if (action.path) {
      // Navigate to path
      navigate(action.path);
    } else if (action.action) {
      // Handle special actions
      console.log('[DASHBOARD] Triggering action:', action.action);
      switch (action.action) {
        case 'openAIChat':
          window.dispatchEvent(new CustomEvent('openRightPane', { detail: 'ai' }));
          break;
        case 'openMessaging':
          window.dispatchEvent(new CustomEvent('openRightPane', { detail: 'messaging' }));
          break;
        case 'openNotifications':
          window.dispatchEvent(new CustomEvent('openRightPane', { detail: 'notifications' }));
          break;
        case 'createTimesheet':
          console.log('[DASHBOARD] Dispatching openTimesheetForm event');
          window.dispatchEvent(new CustomEvent('openTimesheetForm'));
          break;
        case 'createLeave':
          console.log('[DASHBOARD] Dispatching openLeaveForm event');
          window.dispatchEvent(new CustomEvent('openLeaveForm'));
          break;
        case 'createProject':
          console.log('[DASHBOARD] Dispatching openProjectForm event');
          window.dispatchEvent(new CustomEvent('openProjectForm'));
          break;
        default:
          console.log('Unknown action:', action.action);
      }
    }
  };

  // Re-evaluate widgets when language changes to update translations
  useEffect(() => {
    if (quickActions.length > 0 && user && !loading) {
      // Re-merge with defaults to get fresh translations
      const defaults = getDefaultQuickActions();
      setQuickActions(prevActions => {
        return prevActions.map(action => {
          const defaultWidget = defaults.find(d => d.id === action.id);
          if (defaultWidget) {
            // Always update label, description, icon, and color from defaults (which use current language)
            return { 
              ...action, 
              label: defaultWidget.label, // Use translated label
              description: defaultWidget.description, // Use translated description
              icon: defaultWidget.icon || action.icon, // Use default icon if available
              color: defaultWidget.color || action.color // Use default color if available
            };
          }
          // If widget not in defaults, try to get translation via functions
          const translatedLabel = getWidgetLabel(action.id);
          const translatedDesc = getWidgetDescription(action.id);
          return {
            ...action,
            label: translatedLabel || action.label,
            description: translatedDesc || action.description
          };
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, t]);

  const getDefaultQuickActions = (): DashboardWidget[] => {
    console.log('[Dashboard] getDefaultQuickActions - isSystemAdmin:', isSystemAdmin, 'user:', user?.role);
    
    // System Admin gets metric widgets
    if (isSystemAdmin) {
      console.log('[Dashboard] Returning metric widgets for System Admin');
      return [
        {
          id: 'metric-total-users',
          type: 'quickAction',
          label: t('dashboard.metricTotalUsers'),
          icon: 'Users',
          description: t('dashboard.metricUserStats'),
          color: 'bg-accent-500',
          order: 0,
          enabled: true
        },
        {
          id: 'metric-total-accounts',
          type: 'quickAction',
          label: t('dashboard.metricTotalAccounts'),
          icon: 'Briefcase',
          description: t('dashboard.metricAccountStats'),
          color: 'bg-success-500',
          order: 1,
          enabled: true
        },
        {
          id: 'metric-daily-active-users',
          type: 'quickAction',
          label: t('dashboard.metricDailyActiveUsers'),
          icon: 'TrendingUp',
          description: t('dashboard.metricUserEngagement'),
          color: 'bg-purple-500',
          order: 2,
          enabled: true
        },
        {
          id: 'metric-monthly-revenue',
          type: 'quickAction',
          label: t('dashboard.metricMonthlyRevenue'),
          icon: 'FileText',
          description: t('dashboard.metricRevenueTracking'),
          color: 'bg-emerald-500',
          order: 3,
          enabled: true
        },
        {
          id: 'metric-integrations',
          type: 'quickAction',
          label: t('dashboard.metricIntegrations'),
          icon: 'Calendar',
          description: t('dashboard.metricActiveConnections'),
          color: 'bg-primary-500',
          order: 4,
          enabled: true
        }
      ];
    }

    // Regular users get standard widgets
    return [
      {
        id: 'admin',
        type: 'quickAction',
        label: t('navigation.admin'),
        icon: 'TrendingUp',
        path: '/admin',
        description: t('dashboard.widgetSystemSettings'),
        color: 'bg-gray-500',
        order: 0,
        enabled: true
      },
      // Action widgets for dashboard (separate from right menu)
      {
        id: 'dashboard-aiChat',
        type: 'quickAction',
        label: t('dashboard.widgetAIChat'),
        icon: 'MessageCircle',
        action: 'openAIChat',
        description: t('dashboard.widgetOpenAIAssistant'),
        color: 'bg-indigo-500',
        order: 7,
        enabled: false
      },
      {
        id: 'dashboard-createMessage',
        type: 'quickAction',
        label: t('dashboard.widgetCreateMessage'),
        icon: 'Mail',
        action: 'openMessaging',
        description: t('dashboard.widgetSendMessage'),
        color: 'bg-cyan-500',
        order: 8,
        enabled: false
      },
      {
        id: 'dashboard-notifications',
        type: 'quickAction',
        label: t('dashboard.widgetNotifications'),
        icon: 'Bell',
        action: 'openNotifications',
        description: t('dashboard.widgetViewNotifications'),
        color: 'bg-warning-500',
        order: 9,
        enabled: false
      },
      {
        id: 'dashboard-createTimesheet',
        type: 'quickAction',
        label: t('dashboard.widgetCreateTimesheet'),
        icon: 'Clock',
        action: 'createTimesheet',
        description: t('dashboard.widgetAddTimesheet'),
        color: 'bg-teal-500',
        order: 10,
        enabled: false
      },
      {
        id: 'dashboard-createLeave',
        type: 'quickAction',
        label: t('dashboard.widgetCreateLeave'),
        icon: 'Calendar',
        action: 'createLeave',
        description: t('dashboard.widgetCreateLeaveRequest'),
        color: 'bg-primary-500',
        order: 11,
        enabled: false
      },
      {
        id: 'dashboard-createProject',
        type: 'quickAction',
        label: t('dashboard.widgetCreateProject'),
        icon: 'Briefcase',
        action: 'createProject',
        description: t('dashboard.widgetAddProject'),
        color: 'bg-accent-600',
        order: 12,
        enabled: false
      },
      {
        id: 'workflow-process',
        type: 'quickAction',
        label: t('processFlow.title'),
        icon: 'TrendingUp',
        description: t('dashboard.salesPipeline'),
        color: 'bg-warning-500',
        order: 13,
        enabled: true
      }
    ];
  };

  const renderIcon = (iconName: string) => {
    if (!iconName) {
      console.warn('[Dashboard] Missing icon name for widget');
      return null;
    }
    const IconComponent = iconMap[iconName];
    if (!IconComponent) {
      console.warn(`[Dashboard] Icon "${iconName}" not found in iconMap. Available icons:`, Object.keys(iconMap));
      return null;
    }
    return <IconComponent className="w-6 h-6" />;
  };

  // Use useCallback to ensure translations update when language changes
  const getWidgetLabel = React.useCallback((widgetId: string): string => {
    const labelMap: Record<string, string> = {
      'projects': t('navigation.projects'),
      'staff': t('navigation.staff'),
      'timesheets': t('navigation.timesheets'),
      'contacts': t('navigation.contacts'),
      'calendar': t('navigation.calendar'),
      'leave': t('navigation.leave'),
      'admin': t('navigation.admin'),
      'dashboard-aiChat': t('dashboard.widgetAIChat'),
      'dashboard-createMessage': t('dashboard.widgetCreateMessage'),
      'dashboard-notifications': t('dashboard.widgetNotifications'),
      'dashboard-createTimesheet': t('dashboard.widgetCreateTimesheet'),
      'dashboard-createLeave': t('dashboard.widgetCreateLeave'),
      'dashboard-createProject': t('dashboard.widgetCreateProject'),
      'workflow-process': t('dashboard.salesPipeline'),
      'metric-total-users': t('dashboard.metricTotalUsers'),
      'metric-total-accounts': t('dashboard.metricTotalAccounts'),
      'metric-daily-active-users': t('dashboard.metricDailyActiveUsers'),
      'metric-monthly-revenue': t('dashboard.metricMonthlyRevenue'),
      'metric-integrations': t('dashboard.metricIntegrations')
    };
    // Always use translation if available
    const translated = labelMap[widgetId];
    if (translated) return translated;
    // If no translation found, return empty string (never use stored English value)
    console.warn(`[Dashboard] No translation found for widget label: ${widgetId}`);
    return '';
  }, [t]);

  const getWidgetDescription = React.useCallback((widgetId: string): string => {
    const descMap: Record<string, string> = {
      'projects': t('dashboard.widgetManageProjects'),
      'staff': t('dashboard.widgetViewStaff'),
      'timesheets': t('dashboard.widgetTrackTime'),
      'contacts': t('dashboard.widgetManageContacts'),
      'calendar': t('dashboard.widgetViewSchedules'),
      'leave': t('dashboard.widgetManageLeave'),
      'admin': t('dashboard.widgetSystemSettings'),
      'dashboard-aiChat': t('dashboard.widgetOpenAIAssistant'),
      'dashboard-createMessage': t('dashboard.widgetSendMessage'),
      'dashboard-notifications': t('dashboard.widgetViewNotifications'),
      'dashboard-createTimesheet': t('dashboard.widgetAddTimesheet'),
      'dashboard-createLeave': t('dashboard.widgetCreateLeaveRequest'),
      'dashboard-createProject': t('dashboard.widgetAddProject'),
      'workflow-process': t('dashboard.widgetWorkflowProcess'),
      'metric-total-users': t('dashboard.metricUserStats'),
      'metric-total-accounts': t('dashboard.metricAccountStats'),
      'metric-daily-active-users': t('dashboard.metricUserEngagement'),
      'metric-monthly-revenue': t('dashboard.metricRevenueTracking'),
      'metric-integrations': t('dashboard.metricActiveConnections')
    };
    // Always use translation if available
    const translated = descMap[widgetId];
    if (translated) return translated;
    // If no translation found, return empty string (never use stored English value)
    console.warn(`[Dashboard] No translation found for widget description: ${widgetId}`);
    return '';
  }, [t]);

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newActions = [...quickActions];
    const draggedWidget = newActions[draggedIndex];
    newActions.splice(draggedIndex, 1);
    newActions.splice(index, 0, draggedWidget);
    
    setQuickActions(newActions);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleConfigureClick = () => {
    setEditMode(true);
  };

  const toggleWidget = (widgetId: string) => {
    const widgetIndex = quickActions.findIndex(w => w.id === widgetId);
    if (widgetIndex === -1) return;

    const newActions = [...quickActions];
    newActions[widgetIndex].enabled = !newActions[widgetIndex].enabled;
    setQuickActions(newActions);
  };

  const enableWidget = (widgetId: string) => {
    const newActions = quickActions.map(w => 
      w.id === widgetId ? { ...w, enabled: true } : w
    );
    setQuickActions(newActions);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Update order of quickActions
      const updatedQuickActions = quickActions.map((w, idx) => ({ ...w, order: idx }));
      
      // Merge with other widget types
      const otherWidgets = allWidgets.filter(w => w.type !== 'quickAction');
      const allUpdatedWidgets = [...updatedQuickActions, ...otherWidgets];

      const response = await fetch(`${apiUrl}/api/dashboard-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          widgets: allUpdatedWidgets,
          isAccountWide: false
        })
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMessage(t('dashboard.savedSuccessfully'));
        setTimeout(() => setSuccessMessage(null), 3000);
        setEditMode(false);
        await fetchDashboardSettings();
      }
    } catch (err) {
      console.error('Failed to save dashboard:', err);
      alert(t('dashboard.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    fetchDashboardSettings(); // Reload to reset changes
  };

  if (loading || !user) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {t('navigation.dashboard')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {isSystemAdmin ? t('dashboard.systemMetrics') : t('dashboard.welcome')}
          </p>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600 dark:text-gray-400">{t('dashboard.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {t('navigation.dashboard')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {isSystemAdmin ? t('dashboard.systemMetrics') : t('dashboard.welcome')}
          </p>
        </div>
        
        {canEdit && (
          <div className="flex gap-2">
            {!editMode ? (
              <button
                onClick={handleConfigureClick}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
              >
                <Settings className="w-4 h-4" />
                {t('dashboard.configure')}
              </button>
            ) : (
              <>
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {saving ? t('dashboard.saving') : t('dashboard.saveChanges')}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 text-success-700 dark:bg-success-900 dark:border-green-700 dark:text-success-300 rounded-lg">
          {successMessage}
        </div>
      )}

      {editMode && (
        <div className="mb-6 p-4 bg-accent-50 border border-accent-200 text-accent-700 dark:bg-accent-900 dark:border-accent-700 dark:text-accent-300 rounded-lg">
          <p className="font-medium">{t('dashboard.editModeActive')}</p>
          <p className="text-sm mt-1">{t('dashboard.editModeInstructions')}</p>
        </div>
      )}

      {/* Construction Banner */}
      {/* <div className="mb-8">
        <ConstructionBanner />
      </div> */}

      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ${isSystemAdmin ? 'items-stretch' : ''}`}>
        {quickActions.filter(a => a.enabled).map((action, index) => {
          // Get the actual index in the full quickActions array for drag operations
          const actualIndex = quickActions.findIndex(w => w.id === action.id);
          
          const CardContent = () => {
            // Render metric widgets for System Admin
            if (isSystemAdmin && action.id.startsWith('metric-')) {
              return (
                <div className="flex items-start gap-4 w-full">
                  {editMode && (
                    <div className="flex flex-col gap-2">
                      <GripVertical className="w-5 h-5 text-gray-400" />
                    </div>
                  )}
                  <MetricWidget 
                    id={action.id} 
                    color={action.color || 'bg-gray-500'} 
                    metrics={systemMetrics}
                  />
                  {editMode && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleWidget(action.id);
                      }}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title={action.enabled ? t('dashboard.hideWidget') : t('dashboard.showWidget')}
                    >
                      {action.enabled ? (
                        <Eye className="w-5 h-5 text-success-600 dark:text-success-400" />
                      ) : (
                        <EyeOff className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                  )}
                </div>
              );
            }

            // Render workflow process widget
            if (action.id === 'workflow-process') {
              return (
                <div className="flex items-start gap-4 w-full h-full">
                  {editMode && (
                    <div className="flex flex-col gap-2">
                      <GripVertical className="w-5 h-5 text-gray-400" />
                    </div>
                  )}
                  <WorkflowProcessWidget 
                    color={action.color || 'bg-warning-500'} 
                  />
                  {editMode && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleWidget(action.id);
                      }}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title={action.enabled ? t('dashboard.hideWidget') : t('dashboard.showWidget')}
                    >
                      {action.enabled ? (
                        <Eye className="w-5 h-5 text-success-600 dark:text-success-400" />
                      ) : (
                        <EyeOff className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                  )}
                </div>
              );
            }

            // Regular widgets for normal users
            return (
              <div className="flex items-start gap-4">
                {editMode && (
                  <div className="flex flex-col gap-2">
                    <GripVertical className="w-5 h-5 text-gray-400" />
                  </div>
                )}
                
                <div className={`${action.color || 'bg-gray-500'} p-3 rounded-lg text-white ${!editMode && 'group-hover:scale-110'} transition-transform`}>
                  {renderIcon(action.icon)}
                </div>
                
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
                    {getWidgetLabel(action.id) || action.label}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {getWidgetDescription(action.id) || action.description}
                  </p>
                </div>

                {editMode && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleWidget(action.id);
                    }}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title={action.enabled ? t('dashboard.hideWidget') : t('dashboard.showWidget')}
                  >
                    {action.enabled ? (
                      <Eye className="w-5 h-5 text-success-600 dark:text-success-400" />
                    ) : (
                      <EyeOff className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                )}

                {!action.enabled && editMode && (
                  <div className="absolute inset-0 bg-gray-900/10 dark:bg-gray-100/10 flex items-center justify-center rounded-lg">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('dashboard.hidden')}</span>
                  </div>
                )}
              </div>
            );
          };

          const isMetricWidget = isSystemAdmin && action.id.startsWith('metric-');
          const isWorkflowWidget = action.id === 'workflow-process';
          return (
          <div
            key={action.id}
            draggable={editMode ? true : false}
            onDragStart={() => editMode && handleDragStart(actualIndex)}
            onDragOver={(e) => editMode && handleDragOver(e, actualIndex)}
            onDragEnd={handleDragEnd}
            className={`relative ${editMode ? 'cursor-move' : ''} ${isWorkflowWidget ? 'h-full col-span-1 md:col-span-2 lg:col-span-3' : ''}`}
          >
            {editMode ? (
              <div
                className={`card p-4 transition-all duration-200 text-left group w-full hover:shadow-md border-2 border-dashed border-gray-300 dark:border-gray-600 ${
                  !action.enabled ? 'opacity-50 bg-gray-50 dark:bg-gray-700' : ''
                } ${isWorkflowWidget ? 'h-full flex flex-col' : ''}`}
              >
                <CardContent />
              </div>
            ) : (
              <div
                className={`card p-4 transition-all duration-200 text-left group w-full hover:shadow-lg hover:border-primary-500 dark:hover:border-primary-500 ${
                  isMetricWidget ? 'min-h-[140px] flex items-center' : isWorkflowWidget ? 'h-full min-h-[180px] flex flex-col' : 'min-h-[140px] flex items-center'
                } ${
                  !action.enabled ? 'opacity-50 bg-gray-50 dark:bg-gray-700' : ''
                } ${isWorkflowWidget ? '' : 'cursor-pointer'}`}
                onClick={() => !isWorkflowWidget && handleWidgetClick(action)}
              >
                <CardContent />
              </div>
            )}
          </div>
        );
        })}
      </div>

      {editMode && quickActions.filter(a => !a.enabled).length > 0 && (
        <div className="mt-8">
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {t('dashboard.availableWidgets')}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t('dashboard.addWidgetsHint')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {quickActions.filter(a => !a.enabled).map((action) => (
                <div
                  key={`hidden-${action.id}`}
                  className="card p-4 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600"
                >
                  <div className="flex items-center gap-3">
                    <div className={`${action.color || 'bg-gray-500'} p-2 rounded-lg text-white opacity-50`}>
                      {renderIcon(action.icon)}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {getWidgetLabel(action.id) || action.label}
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {getWidgetDescription(action.id) || action.description}
                      </p>
                    </div>
                    <button
                      onClick={() => enableWidget(action.id)}
                      className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                      title={t('dashboard.addWidgetToDashboard')}
                    >
                      <Plus className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
